import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiUrlProvider } from './api-url.provider';
import { Observable } from 'rxjs';
import { eachDayOfInterval, format, differenceInDays } from 'date-fns';
import { IRosterCode } from '../shared/models/roster-code';
import { IApiResult } from '../shared/models/api-result';
import { IHoliday } from '../shared/models/holiday';
import { IEmployeeRosterUpdateByRuleModel } from '../shared/models/roster/employee-roster-update-by-rule-model';
import { IRosterResult, IRosterResultWithEntities } from "../shared/models/roster/roster-result";
import { IRosterNote } from '../shared/models/roster/roster-note';
import { IRosterSummaryLine } from '../shared/models/roster/roster-summary-line';
import { RosterDataGenerationModel } from '../shared/models/roster/roster-data-generation-model';
import { IEmployeeRosterUpdateModel } from "../shared/models/roster/employee-roster-update-model";
import { IDateRange } from '../shared/models/date-range';
import { IDummyRequest } from '../shared/models/dummy-request';
import { IEmployeeRoster } from '../shared/models/roster/employee-roster';
import { IEmployeeHighlightUpdate, IRosterEmployeeHighlight } from '../shared/models/roster/employee-highlight';
import { IInvalidEmployeeRoster } from '../shared/models/roster/invalid-employee-roster';
import { IPayrollRosterCodeUpdateModel } from '../shared/models/roster/payroll-roster-code-update-mode';
import { IRosterDataClearModel } from '../shared/models/roster/roster-data-clear-model';
import { IRosterEmployeeNote } from '../shared/models/roster/roster-employee-note';


@Injectable({
  providedIn: 'root'
})
export class RosterService {
  private firstPayDay: Date = new Date(2020, 6, 8);
  
  constructor(
    private http: HttpClient,
    private apiUrlProvider: ApiUrlProvider) { }

  getRosterCodes(): Observable<IApiResult<IRosterCode[]>>{
    return this.http.get<IApiResult<IRosterCode[]>>(`${this.apiUrlProvider.url}/RosterCode`);
  }

  getHolidays(year: number): Observable<IApiResult<IHoliday[]>>{
    return this.http.get<IApiResult<IHoliday[]>>(`${this.apiUrlProvider.url}/Holiday`);
  }

  getRosterSummaryLines(): Observable<IApiResult<IRosterSummaryLine[]>>{
    return this.http.get<IApiResult<IRosterSummaryLine[]>>(`${this.apiUrlProvider.url}/RosterSummaryLine`);
  }

  updateRoster(employeeRosterUpdateModel: IEmployeeRosterUpdateModel): Observable<IApiResult<IRosterResult>>{
    return this.http.post<IApiResult<IRosterResult>>(`${this.apiUrlProvider.url}/EmployeeRoster`, employeeRosterUpdateModel);
  }

  highlightEmployeeSection(update: IEmployeeHighlightUpdate): Observable<IApiResult<IRosterEmployeeHighlight[]>>{
    return this.http.post<IApiResult<IRosterEmployeeHighlight[]>>(`${this.apiUrlProvider.url}/EmployeeRoster/HighlightEmployee`, update);
  }

  getHighlighedRosterEmployee(): Observable<IApiResult<IRosterEmployeeHighlight[]>>{
    return this.http.get<IApiResult<IRosterEmployeeHighlight[]>>(`${this.apiUrlProvider.url}/EmployeeRoster/HighlightEmployee`)
  }

  updateRosterNote(rosterNote: IRosterNote): Observable<IApiResult<IRosterNote>>{
    return this.http.post<IApiResult<IRosterNote>>(`${this.apiUrlProvider.url}/RosterNote`, rosterNote);
  }

  batchUpdateRoster(updateByRuleModel: IEmployeeRosterUpdateByRuleModel): Observable<IApiResult<IRosterResult>>{
    return this.http.post<IApiResult<IRosterResult>>(`${this.apiUrlProvider.url}/EmployeeRoster/BatchUpdate`, updateByRuleModel);
  }

  batchUpdateRoster1(updateByRuleModel: IEmployeeRosterUpdateByRuleModel): Observable<IApiResult<string>>{
    return this.http.post<IApiResult<string>>(`${this.apiUrlProvider.url}/EmployeeRoster/BatchUpdate`, updateByRuleModel);
  }

  generateRosterData(rosterProjectId: number, rosterDataGenerationModel: RosterDataGenerationModel): Observable<IApiResult<string>>{
    return this.http.post<IApiResult<string>>(`${this.apiUrlProvider.url}/EmployeeRoster/${rosterProjectId}/GenerateRosterData`, rosterDataGenerationModel);
  }

  deleteFutureRosterData(rosterProjectId: number, rosterDataClearModel: IRosterDataClearModel): Observable<IApiResult<number>>{
    return this.http.post<IApiResult<number>>(`${this.apiUrlProvider.url}/EmployeeRoster/${rosterProjectId}/ClearRosterData`, rosterDataClearModel);
  }

  getInvalidEmployeeRosters(rosterProjectId: number, from:Date, to:Date): Observable<IApiResult<IInvalidEmployeeRoster[]>>{
    let params = new HttpParams()
      .set('from', format(from, "yyyy-MM-dd"))
      .set('to', format(to, "yyyy-MM-dd"));

    return this.http.get<IApiResult<IInvalidEmployeeRoster[]>>(`${this.apiUrlProvider.url}/EmployeeRoster/${rosterProjectId}/InvalidRosters`, {params});
  }

  getEmployeeRosterByEmployees(day: Date, employeeList: number[]): Observable<IApiResult<IEmployeeRoster[]>>{
    let params = new HttpParams()
      .set('day', format(day, "yyyy-MM-dd"))
      .set('ids', employeeList.join(","));

    return this.http.get<IApiResult<IEmployeeRoster[]>>(`${this.apiUrlProvider.url}/EmployeeRoster/Employees`, {params});
  }

  /*
  getRosters(from: Date, to: Date, showInRoster?: boolean, activeEmployee?: boolean):Observable<IApiResult<IRosterResult>>{
    let params = new HttpParams()
      .set('from', format(from, "yyyy-MM-dd"))
      .set('to', format(to, "yyyy-MM-dd"));
    
    if (showInRoster !== null){
      params = params.set("showInRoster", showInRoster + "");
    }

    if (activeEmployee !== null){
      params = params.set("activeEmployee", activeEmployee + "");
    }

    return this.http.get<IApiResult<IRosterResult>>(`${this.apiUrlProvider.url}/EmployeeRoster`, {params});
  }
  */

  getRostersWithEntities(rosterProjectId: number, from: Date, to: Date, withEntities: boolean, widhtEmployeeEntities: boolean, showInRoster?: boolean, activeEmployee?: boolean):Observable<IApiResult<IRosterResultWithEntities>>{
    if (!withEntities){
      widhtEmployeeEntities = false;
    }

    let params = new HttpParams()
      .set('from', format(from, "yyyy-MM-dd"))
      .set('to', format(to, "yyyy-MM-dd"))
      .set("withEntities", withEntities + "")
      .set("withEmployeeEntities", widhtEmployeeEntities + "");

    if (showInRoster !== null){
      params = params.set("showInRoster", showInRoster + "");
    }

    if (activeEmployee !== null){
      params = params.set("activeEmployee", activeEmployee + "");
    }

    return this.http.get<IApiResult<IRosterResultWithEntities>>(`${this.apiUrlProvider.url}/EmployeeRosterWithEntities/${rosterProjectId}`, {params});
  }

  getRoster(startDate: Date, endDate: Date){
    let allYearDays = eachDayOfInterval({
     start: startDate, end: endDate
    });

    return allYearDays.map( _day => {
      return {
        name: format(_day, 'yyyyMMdd'),
        year: _day.getFullYear(),
        month: _day.getMonth() + 1,
        day: _day.getDate(),
        rosterCode: '',
        ticketStatusId: 0,
        colorCode:"#ffffff"
      }
    });
  }

  updateTravelStatus(rosterProjectId: number, updateTravelStatusModel):Observable<IApiResult<IEmployeeRoster>>{
    let url = `${this.apiUrlProvider.url}/EmployeeRoster/${rosterProjectId}/UpdateTravelStatus`;
    return this.http.post<IApiResult<IEmployeeRoster>>(url, updateTravelStatusModel);
  }

  updatePayrolLRosterCode(rosterProjectId: number, payrollRosterCodeUpdateModelList: IPayrollRosterCodeUpdateModel[]):Observable<IApiResult<number>>{
    let url = `${this.apiUrlProvider.url}/EmployeeRoster/${rosterProjectId}/UpdatePayrollRosterCode`;
    return this.http.post<IApiResult<number>>(url, payrollRosterCodeUpdateModelList);
  }

  generateDailySignonSheet(rosterProjectId: number, dr: IDateRange, whenDone, whenError){
    let url = `${this.apiUrlProvider.url}/SignOnSheet?from=${format(dr.range.from, "MM/dd/yyyy")}&to=${format(dr.range.to, "MM/dd/yyyy")}&rosterProjectId=${rosterProjectId}`;
    let suffix = (dr.range.from === dr.range.to) ? format(dr.range.from, "ddMMyyyy") : `${format(dr.range.from, "ddMMyyyy")}_${format(dr.range.to, "ddMMyyyy")}`;
    let filename = `DailySignonSheet_${suffix}.pdf`;

    return this.http
      .get(url, {responseType: 'blob'})
      .subscribe(res => {
        if (res.type === "application/json"){
          let fr = new FileReader();
          fr.readAsText(res);
          fr.onload = (evt) => {
            if (whenError){
              whenError(JSON.parse(evt.target.result as string));
            }
          }
        }else{
          let objectUrl = window.URL.createObjectURL(res);
          let imageAnchor: HTMLAnchorElement = document.createElement("a");
          imageAnchor.href = objectUrl;
          imageAnchor.target = "_blank";
          imageAnchor.download = filename;
          imageAnchor.click();
          window.URL.revokeObjectURL(objectUrl);
        }
      }, _ => {})
      .add(()=>{
        if (whenDone){
          whenDone();
        }
      });    
  }

  exportRoster(rosterProjectId: number, dr: IDateRange, activeRoster:boolean, whenDone, whenError){
    let url = `${this.apiUrlProvider.url}/ExportRosterData/${rosterProjectId}?from=${format(dr.range.from, "MM/dd/yyyy")}&to=${format(dr.range.to, "MM/dd/yyyy")}&activeRoster=${activeRoster}`;
    let suffix = (dr.range.from === dr.range.to) ? format(dr.range.from, "ddMMyyyy") : `${format(dr.range.from, "ddMMyyyy")}_${format(dr.range.to, "ddMMyyyy")}`;
    let filename = `Roster_${suffix}.xlsx`;

    return this.http
      .get(url, {responseType: 'blob'})
      .subscribe(res => {
        if (res.type === "application/json"){
          let fr = new FileReader();
          fr.readAsText(res);
          fr.onload = (evt) => {
            if (whenError){
              whenError(JSON.parse(evt.target.result as string));
            }
          }
        }else{
          let objectUrl = window.URL.createObjectURL(res);
          let imageAnchor: HTMLAnchorElement = document.createElement("a");
          imageAnchor.href = objectUrl;
          imageAnchor.target = "_blank";
          imageAnchor.download = filename;
          imageAnchor.click();
          window.URL.revokeObjectURL(objectUrl);
        }
      }, _ => {})
      .add(()=>{
        if (whenDone){
          whenDone();
        }
      });    
  }

  resetRosterData(){
    var dummyData: IDummyRequest = {
      dummyData: ""
    }
    return this.http.post<IApiResult<string>>(`${this.apiUrlProvider.url}/ResetRosterData`, dummyData);
  }

  importRoster(whenDone = null, whenError = null){
    let url = `${this.apiUrlProvider.url}/RosterImport`;

    this.http
      .get(url, {responseType: 'blob'})
      .subscribe(res => {
        if (res.type === "application/json"){
          let fr = new FileReader();
          fr.readAsText(res);
          fr.onload = (evt) => {
            if (whenError){
              whenError(JSON.parse(evt.target.result as string));
            }
          }
        }else{
          let objectUrl = window.URL.createObjectURL(res);
          let imageAnchor: HTMLAnchorElement = document.createElement("a");
          imageAnchor.href = objectUrl;
          imageAnchor.target = "_blank";
          imageAnchor.download = `roster-import-issue-report.xlsx`;
          imageAnchor.click();
          window.URL.revokeObjectURL(objectUrl);
        }
      }, _ => {})
      .add(()=>{
        if (whenDone){
          whenDone();
        }
      });
  }

  importRosterFromCsv(){
    var dummyData: IDummyRequest = {
      dummyData: ""
    }
    return this.http.post<IApiResult<string>>(`${this.apiUrlProvider.url}/ImportRosterFromCsv`, dummyData);
  }

  isPayDay(day: Date): boolean{
    let daysDiff = differenceInDays(day, this.firstPayDay);

    if (daysDiff % 14 === 0){
      return true;
    }

    return false;
  }

  hasTicketStatus(rosterCode: string){
    return rosterCode === "FIA" || 
      rosterCode === "FIP" || 
      rosterCode === "FOA" || 
      rosterCode === "FOP";
  }

  updateRosterEmployeeNote(rosterEmployeeNote: IRosterEmployeeNote):Observable<IApiResult<IRosterEmployeeNote>>{
    let url = `${this.apiUrlProvider.url}/EmployeeRoster/UpdateRosterEmployeeNote`;
    return this.http.post<IApiResult<IRosterEmployeeNote>>(url, rosterEmployeeNote);
  }

  getColumnName(colId: string): string{
    return {
      "employeeName": "Employee Name",
      "company": "Company",
      "position": "Position",
      "supervisor": "Supervisor",
      "phone": "Contact",
      "accessId": "Access ID",
      "workarea": "Work Area",
      "email": "Email",
      "dateOfBirth": "DOB"
    }[colId];
  }
}
